import { useState, useEffect, useMemo, useCallback } from 'react';
import { otaApi, type OtaDevice } from '../api/ota';
import { wattelzceApi, type KpiData } from '../api/wattelzce';
import { startTransaction, stopTransaction, getDeviceStatus, getChargingSessionStatus } from '../api/deviceService';
import { architectureApi } from '../api/architecture';
import { getOrgBornes, addOrgBorne, removeOrgBorne, type OrgBorne } from '../api/bornesService';
import { useAuth } from '../context/AuthContext';
import ChartTimeFilter, { type TimeFilterValue } from '../components/wattelEM/ChartTimeFilter';
import { useI18n, translate } from '../i18n';

type TFn = ReturnType<typeof useI18n>['t'];

// ─── Persisted charging sessions (localStorage) ─────────────────────
// Survives page navigation, refresh, logout. Keyed by device_sn.
const SESSION_KEY = 'zce_borne_sessions_v1';

interface PersistedSession {
  deviceSn: string;
  transactionId: number;
  chargeStartedAt: number;          // ms epoch
  startEnergyWh: number | null;     // meter reading at start (activeFEnergy / 10_000)
  lastEnergyWh: number | null;      // last seen meter reading
  sessionEnergyWh: number;          // delta consumed during this session
  lastTickAt: number;               // last update timestamp
  fallbackPowerW: number;           // simulation power if no real meter
}

function loadSessions(): Record<string, PersistedSession> {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
}

function saveSessions(sessions: Record<string, PersistedSession>) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(sessions)); } catch { /* quota */ }
}

function updateSession(deviceSn: string, patch: Partial<PersistedSession>) {
  const all = loadSessions();
  if (!all[deviceSn]) return;
  all[deviceSn] = { ...all[deviceSn], ...patch };
  saveSessions(all);
}

function startSession(s: PersistedSession) {
  const all = loadSessions();
  all[s.deviceSn] = s;
  saveSessions(all);
}

function endSession(deviceSn: string) {
  const all = loadSessions();
  delete all[deviceSn];
  saveSessions(all);
}

interface BorneDevice extends OtaDevice {
  activeTransactionId: number | null;
  currentPowerW: number;
  ocppStatus: string | null;
  chargeStartedAt?: number | null;     // timestamp ms when charge started
  sessionEnergyWh?: number;            // accumulated energy this session (Wh)
  lastEnergyTickAt?: number | null;    // last integration timestamp
}

function formatPower(w: number): string {
  if (w >= 1000) return `${(w / 1000).toFixed(2)} kW`;
  return `${w.toFixed(0)} W`;
}

function getDeviceImg(sn: string): string | null {
  const s = (sn || '').toLowerCase();
  if (s.includes('go') || s.includes('-go-')) return '/devices/wattelgo.svg';
  if (s.includes('wall') || s.includes('-wl-') || s.includes('wl')) return '/devices/wattelwall.svg';
  return null;
}

function getStatusInfo(status: string | null, isCharging: boolean, t: TFn) {
  if (isCharging) {
    return { label: t('chg_status_charging'), bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500 animate-pulse' };
  }
  switch (status) {
    case 'active':
    case 'Available':
    case 'online':
      return { label: t('chg_status_available'), bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500 animate-pulse' };
    case 'faulted':
    case 'Faulted':
      return { label: t('chg_status_error'), bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' };
    case 'inactive':
    case 'Unavailable':
    case 'offline':
      return { label: t('chg_status_offline'), bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' };
    default:
      return { label: status || t('chg_status_unknown'), bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' };
  }
}


function BorneCard({ device, selected, onClick, onRemove }: {
  device: BorneDevice; selected: boolean; onClick: () => void; onRemove?: () => void;
}) {
  const { t } = useI18n();
  const isCharging = device.activeTransactionId !== null;
  const statusInfo = getStatusInfo(device.ocppStatus || device.device_status, isCharging, t);
  const img = getDeviceImg(device.device_sn);
  const modelLabel = img?.includes('wattelgo') ? 'WattelGo' : img?.includes('wattelwall') ? 'WattelWall' : t('chg_model_generic');

  return (
    <div
      className={`relative w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 cursor-pointer ${
        selected
          ? 'border-cyan-500 bg-gradient-to-br from-cyan-50 to-slate-50 shadow-md shadow-cyan-100'
          : 'border-slate-100 bg-white hover:border-cyan-300 hover:shadow-md hover:shadow-slate-100'
      }`}
      onClick={onClick}
    >
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors"
          title={t('chg_remove_title')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
      <div className="flex items-center gap-3 mb-3">
        {img ? (
          <div className="shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-sky-100 to-blue-50 border border-sky-200 flex items-center justify-center p-1.5">
            <img src={img} alt={modelLabel} className="h-full w-full object-contain" />
          </div>
        ) : (
          <div className="shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white shadow-sm">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-zce-navy text-sm truncate">{device.device_name || device.device_sn}</p>
          <p className="text-xs text-zce-gray-muted truncate font-mono">{device.device_sn}</p>
          <p className="text-xs text-zce-gray-muted">{modelLabel}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${statusInfo.bg} ${statusInfo.text}`}>
          <span className={`w-2 h-2 rounded-full ${statusInfo.dot}`} />
          {statusInfo.label}
        </span>
        {device.currentPowerW > 0 && (
          <span className="text-xs font-bold text-zce-orange">{formatPower(device.currentPowerW)}</span>
        )}
      </div>
    </div>
  );
}

function EnergyChart({ filter, deviceId, deviceLabel }: { filter: TimeFilterValue; deviceId: number | null; deviceLabel: string | null }) {
  const { t, locale } = useI18n();
  const { count, formatLabel, unit } = useMemo(() => {
    const dayUnit = t('chg_unit_day');
    const weekdays = t('chg_weekdays').split(',');
    if (filter.range === '24h') return { count: 24, formatLabel: (i: number) => `${i}h`, unit: 'h' };
    if (filter.range === '7d') return { count: 7, formatLabel: (i: number) => weekdays[i] || t('chg_day_fallback', { n: i }), unit: dayUnit };
    if (filter.range === '30d') return { count: 30, formatLabel: (i: number) => `${i + 1}`, unit: dayUnit };
    if (filter.range === '90d') return { count: 90, formatLabel: (i: number) => `${i + 1}`, unit: dayUnit };
    // custom
    const days = Math.max(1, filter.days || 30);
    if (filter.startDate) {
      const start = new Date(filter.startDate + 'T00:00:00');
      return {
        count: days,
        formatLabel: (i: number) => {
          const d = new Date(start);
          d.setDate(start.getDate() + i);
          return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
        },
        unit: dayUnit,
      };
    }
    return { count: days, formatLabel: (i: number) => `${i + 1}`, unit: dayUnit };
  }, [filter, t, locale]);

  const [data, setData] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasRealData, setHasRealData] = useState(false);

  // Fetch real meter values from backend when device or filter changes
  useEffect(() => {
    if (!deviceId) {
      setData([]);
      setHasRealData(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        let series: number[] = [];
        if (filter.range === '24h') {
          const res = await architectureApi.getMeterValuesHourly(deviceId, 24);
          if (res.success && res.data) {
            // res.data is array of { day, daily_kwh, ... } — for hourly, day is a timestamp
            // We bucket into 24 slots based on hour of day
            const buckets = new Array(24).fill(0);
            res.data.forEach((row: any) => {
              const d = new Date(row.day);
              const h = d.getHours();
              if (h >= 0 && h < 24) buckets[h] += Number(row.daily_kwh || 0);
            });
            series = buckets;
          }
        } else {
          const isCustom = filter.range === 'custom' && filter.startDate && filter.endDate;
          const days = isCustom
            ? Math.max(1, filter.days)
            : filter.range === '7d' ? 7 : filter.range === '30d' ? 30 : 90;
          const res = await architectureApi.getMeterValuesHistory(
            deviceId,
            days,
            isCustom ? filter.startDate : undefined,
            isCustom ? filter.endDate : undefined,
          );
          if (res.success && res.data) {
            // Backend returns sorted ascending list of { day, daily_kwh, ... }
            // Fill missing days with 0 over the range
            const map = new Map<string, number>();
            res.data.forEach((row: any) => {
              const k = String(row.day).slice(0, 10);
              map.set(k, Number(row.daily_kwh || 0));
            });
            const buckets: number[] = [];
            if (isCustom && filter.startDate) {
              // Iterate startDate → endDate inclusive
              const start = new Date(filter.startDate + 'T00:00:00');
              for (let i = 0; i < days; i++) {
                const d = new Date(start);
                d.setDate(start.getDate() + i);
                const k = d.toISOString().slice(0, 10);
                buckets.push(map.get(k) || 0);
              }
            } else {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              for (let i = days - 1; i >= 0; i--) {
                const d = new Date(today);
                d.setDate(today.getDate() - i);
                const k = d.toISOString().slice(0, 10);
                buckets.push(map.get(k) || 0);
              }
            }
            series = buckets;
          }
        }
        if (!cancelled) {
          const hasReal = series.some(v => v > 0);
          setData(series.length ? series : new Array(count).fill(0));
          setHasRealData(hasReal);
        }
      } catch (e) {
        console.error('[EnergyChart] fetch error:', e);
        if (!cancelled) {
          setData(new Array(count).fill(0));
          setHasRealData(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deviceId, filter, count]);

  // ⚠ TOUS les hooks DOIVENT être appelés avant tout return conditionnel (React rules)
  const totalKwh = data.reduce((s, v) => s + v, 0);
  const avgKwh = data.length > 0 ? totalKwh / data.length : 0;
  const maxDelta = Math.max(...data, 0);

  // Cumulative series for the curve: running sum of per-bucket consumption
  // so the chart shows total energy consumed over time instead of the variation per bucket
  const cumData = useMemo(() => {
    let acc = 0;
    return data.map(v => (acc += Number(v) || 0));
  }, [data]);
  const maxVal = Math.max(cumData[cumData.length - 1] || 0, 1);

  // Chart dimensions
  const W = 1000;
  const H = 280;
  const PAD_L = 50;
  const PAD_R = 20;
  const PAD_T = 20;
  const PAD_B = 40;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const points = useMemo(() => cumData.map((v, i) => ({
    x: PAD_L + (i / Math.max(1, cumData.length - 1)) * innerW,
    y: PAD_T + innerH - ((v - 0) / (maxVal * 1.15)) * innerH,
    val: v,
  })), [cumData, maxVal, innerW, innerH]);

  const linePath = useMemo(() => {
    if (points.length < 2) return '';
    const path: string[] = [`M ${points[0].x},${points[0].y}`];
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] || points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      path.push(`C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`);
    }
    return path.join(' ');
  }, [points]);

  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Maintenant on peut faire les returns conditionnels
  if (!deviceId) {
    return (
      <div className="h-[260px] flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3 text-slate-400">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
        </div>
        <p className="text-zce-navy font-semibold">{t('chg_select_charger')}</p>
        <p className="text-xs text-zce-gray-muted mt-1">{t('chg_select_charger_chart_hint')}</p>
      </div>
    );
  }

  if (loading && data.length === 0) {
    return (
      <div className="h-[260px] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-zce-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  const areaPath = points.length > 0
    ? `${linePath} L ${points[points.length - 1].x},${PAD_T + innerH} L ${points[0].x},${PAD_T + innerH} Z`
    : '';

  const yTicks = 4;

  return (
    <div className="space-y-3">
      {/* Stats summary */}
      <div className="flex items-center gap-6 text-sm flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm bg-gradient-to-br from-zce-blue to-zce-blue-light shadow-sm" />
          <span className="text-zce-gray-muted">{t('chg_total')}</span>
          <span className="font-bold text-zce-navy tabular-nums">{totalKwh.toFixed(2)} kWh</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zce-gray-muted">{t('chg_average')}</span>
          <span className="font-bold text-zce-navy tabular-nums">{avgKwh.toFixed(2)} kWh/{unit}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zce-gray-muted">{t('chg_peak')}</span>
          <span className="font-bold text-zce-orange tabular-nums">{maxDelta.toFixed(2)} kWh</span>
        </div>
        {!hasRealData && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full ml-auto">
            {t('chg_no_data')}
          </span>
        )}
        {hasRealData && deviceLabel && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full ml-auto">
            ● {t('chg_real_data')}
          </span>
        )}
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[260px]" preserveAspectRatio="none">
          <defs>
            <linearGradient id="energyAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#1e88e5" stopOpacity="0.45" />
              <stop offset="60%" stopColor="#1e88e5" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#1e88e5" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="energyLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0d3b66" />
              <stop offset="50%" stopColor="#1e88e5" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
            <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Y grid lines + labels */}
          {Array.from({ length: yTicks + 1 }).map((_, i) => {
            const y = PAD_T + (i / yTicks) * innerH;
            const value = (maxVal * 1.15) * (1 - i / yTicks);
            return (
              <g key={`grid-${i}`}>
                <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#f1f5f9" strokeWidth="1" strokeDasharray={i === yTicks ? '0' : '4 4'} />
                <text x={PAD_L - 10} y={y + 4} textAnchor="end" className="fill-slate-400" fontSize="11" fontWeight="500">
                  {value.toFixed(value > 10 ? 0 : 1)}
                </text>
              </g>
            );
          })}

          {/* Average reference line removed: cumulative curve makes a per-bucket average horizontal line meaningless */}

          {/* Area + line */}
          {points.length > 1 && (
            <>
              <path d={areaPath} fill="url(#energyAreaGrad)">
                <animate attributeName="opacity" from="0" to="1" dur="0.8s" fill="freeze" />
              </path>
              <path d={linePath} fill="none" stroke="url(#energyLineGrad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" filter="url(#lineGlow)" />
            </>
          )}

          {/* Data points */}
          {points.map((p, i) => (
            <g key={`pt-${i}`}>
              {hoverIdx === i && (
                <circle cx={p.x} cy={p.y} r="10" fill="#1e88e5" opacity="0.18" />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r={hoverIdx === i ? 6 : 4}
                fill="white"
                stroke="#1e88e5"
                strokeWidth="2.5"
                style={{ transition: 'r 0.15s' }}
              />
              <rect
                x={p.x - innerW / Math.max(1, data.length) / 2}
                y={PAD_T}
                width={innerW / Math.max(1, data.length)}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                style={{ cursor: 'pointer' }}
              />
            </g>
          ))}

          {/* X labels */}
          {points.map((p, i) => {
            const showLabel = data.length <= 12 || i % Math.ceil(data.length / 10) === 0 || i === data.length - 1;
            if (!showLabel) return null;
            return (
              <text
                key={`xl-${i}`}
                x={p.x}
                y={H - PAD_B + 22}
                textAnchor="middle"
                className="fill-slate-500"
                fontSize="11"
                fontWeight="500"
              >
                {formatLabel(i)}
              </text>
            );
          })}

          {/* Tooltip */}
          {hoverIdx !== null && points[hoverIdx] && (() => {
            const p = points[hoverIdx];
            const tooltipW = 110;
            const tooltipH = 50;
            const tx = Math.max(PAD_L, Math.min(W - PAD_R - tooltipW, p.x - tooltipW / 2));
            const ty = Math.max(PAD_T, p.y - tooltipH - 14);
            return (
              <g style={{ pointerEvents: 'none' }}>
                <line x1={p.x} y1={PAD_T} x2={p.x} y2={PAD_T + innerH} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
                <rect x={tx} y={ty} width={tooltipW} height={tooltipH} rx="8" fill="#0d3b66" />
                <text x={tx + tooltipW / 2} y={ty + 18} textAnchor="middle" className="fill-white/70" fontSize="10" fontWeight="600">
                  {formatLabel(hoverIdx)}
                </text>
                <text x={tx + tooltipW / 2} y={ty + 38} textAnchor="middle" className="fill-white" fontSize="14" fontWeight="800">
                  {p.val.toFixed(p.val > 10 ? 1 : 3)} kWh
                </text>
              </g>
            );
          })()}
        </svg>

        <div className="absolute top-1 right-2 flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-gradient-to-r from-zce-navy to-emerald-500 rounded-full" />
            <span className="text-zce-gray-muted font-medium">{t('chg_energy_cumulative')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Circular gauge for power (SVG) — pro instrument panel look
function PowerGauge({ powerW, maxW = 22000, charging }: { powerW: number; maxW?: number; charging: boolean }) {
  const { t: tr } = useI18n();
  const pct = Math.min(1, Math.max(0, powerW / maxW));
  // 3/4 arc (270°) sweeping from -135° to +135°
  const startAngle = 135;
  const sweepAngle = 270;
  const radius = 78;
  const arcLength = (sweepAngle / 360) * (2 * Math.PI * radius);
  const fullCircum = 2 * Math.PI * radius;
  const dashArray = `${arcLength} ${fullCircum}`;
  const offset = arcLength * (1 - pct);
  const kw = powerW / 1000;
  // Needle angle: from -135° (0) to +135° (max)
  const needleAngle = startAngle + pct * sweepAngle;

  return (
    <div className="relative w-[220px] h-[220px] mx-auto">
      {/* Outer glow when charging */}
      {charging && (
        <div className="absolute inset-0 rounded-full bg-emerald-400/25 blur-3xl animate-pulse" />
      )}
      <svg viewBox="0 0 200 200" className="relative w-full h-full">
        <defs>
          <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="55%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
          <radialGradient id="dialBg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1f4e7a" />
            <stop offset="70%" stopColor="#143150" />
            <stop offset="100%" stopColor="#0d2238" />
          </radialGradient>
          <filter id="needleShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
            <feOffset dx="0" dy="1" result="offsetblur" />
            <feComponentTransfer><feFuncA type="linear" slope="0.45" /></feComponentTransfer>
            <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Dark dial background */}
        <circle cx="100" cy="100" r="92" fill="url(#dialBg)" stroke="#2a5a8a" strokeWidth="2" />
        <circle cx="100" cy="100" r="88" fill="none" stroke="#3a6a9a" strokeWidth="0.5" opacity="0.5" />

        {/* Background arc (full track, dimmed) */}
        <g transform="rotate(-90 100 100)">
          <circle
            cx="100" cy="100" r={radius}
            fill="none"
            stroke="#1f2937"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={dashArray}
            transform={`rotate(${startAngle - 90} 100 100)`}
          />
          {/* Active gradient arc */}
          <circle
            cx="100" cy="100" r={radius}
            fill="none"
            stroke="url(#gaugeGrad)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={dashArray}
            strokeDashoffset={offset}
            transform={`rotate(${startAngle - 90} 100 100)`}
            style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)' }}
          />
        </g>

        {/* Tick marks (270° arc) */}
        {Array.from({ length: 28 }).map((_, i) => {
          const t = i / 27;
          const angle = startAngle + t * sweepAngle;
          const isMajor = i % 9 === 0 || i === 27;
          const isCenter = i === 13 || i === 14;
          const r1 = radius - (isMajor ? 22 : isCenter ? 19 : 17);
          const r2 = radius - 14;
          const rad = (angle * Math.PI) / 180;
          const x1 = 100 + r1 * Math.cos(rad);
          const y1 = 100 + r1 * Math.sin(rad);
          const x2 = 100 + r2 * Math.cos(rad);
          const y2 = 100 + r2 * Math.sin(rad);
          return (
            <line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={isMajor ? '#94a3b8' : '#475569'}
              strokeWidth={isMajor ? 1.8 : 1}
              strokeLinecap="round"
            />
          );
        })}

        {/* Scale labels (0 / kW max midpoints) */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const angle = startAngle + t * sweepAngle;
          const rad = (angle * Math.PI) / 180;
          const lr = radius - 32;
          const lx = 100 + lr * Math.cos(rad);
          const ly = 100 + lr * Math.sin(rad);
          const val = ((maxW / 1000) * t).toFixed(0);
          return (
            <text
              key={i}
              x={lx} y={ly + 3}
              textAnchor="middle"
              fontSize="8"
              fontWeight="700"
              fill="#64748b"
              fontFamily="ui-monospace, monospace"
            >
              {val}
            </text>
          );
        })}

        {/* Needle */}
        <g
          transform={`rotate(${needleAngle - 90} 100 100)`}
          style={{ transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)' }}
          filter="url(#needleShadow)"
        >
          <polygon
            points="100,38 96,100 104,100"
            fill={charging ? '#10b981' : '#94a3b8'}
          />
          <circle cx="100" cy="100" r="7" fill="#0f172a" stroke="#475569" strokeWidth="1.5" />
          <circle cx="100" cy="100" r="2.5" fill={charging ? '#10b981' : '#64748b'} />
        </g>
      </svg>

      {/* Center digital readout (below needle pivot) */}
      <div className="absolute inset-x-0 bottom-4 flex flex-col items-center pointer-events-none">
        <div className="px-3 py-1 rounded-lg bg-slate-950/80 border border-slate-700/60 backdrop-blur-sm">
          <div className="flex items-baseline gap-1">
            <span className={`text-2xl font-extrabold tabular-nums leading-none ${charging ? 'text-emerald-300' : 'text-slate-300'}`} style={{ fontFamily: 'ui-monospace, monospace', textShadow: charging ? '0 0 12px rgba(16,185,129,0.5)' : 'none' }}>
              {kw.toFixed(2)}
            </span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">kW</span>
          </div>
        </div>
        {charging && (
          <div className="flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(16,185,129,0.8)]" />
            <span className="text-[9px] font-bold text-emerald-300 uppercase tracking-widest">{tr('pg_live_badge')}</span>
          </div>
        )}
      </div>

      {/* Top label */}
      <div className="absolute inset-x-0 top-6 flex justify-center pointer-events-none">
        <span className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold">{tr('chg_instant_power')}</span>
      </div>
    </div>
  );
}

// Mini stat tile for the meter dashboard — dark instrument panel style
function MeterStat({ label, value, unit, color, icon }: {
  label: string; value: string; unit: string; color: 'orange' | 'blue' | 'emerald' | 'navy'; icon: React.ReactNode;
}) {
  const colors = {
    orange: { text: 'text-amber-300', glow: 'shadow-[0_0_10px_rgba(251,191,36,0.25)]', iconBg: 'bg-amber-500/15 text-amber-400 ring-amber-500/30' },
    blue: { text: 'text-sky-300', glow: 'shadow-[0_0_10px_rgba(56,189,248,0.25)]', iconBg: 'bg-sky-500/15 text-sky-400 ring-sky-500/30' },
    emerald: { text: 'text-emerald-300', glow: 'shadow-[0_0_10px_rgba(16,185,129,0.3)]', iconBg: 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30' },
    navy: { text: 'text-slate-200', glow: '', iconBg: 'bg-slate-700/40 text-slate-300 ring-slate-600/40' },
  }[color];
  return (
    <div className="relative rounded-xl bg-[#143150]/70 border border-[#2a5a8a]/60 backdrop-blur p-3 overflow-hidden">
      {/* subtle top highlight */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="flex items-start justify-between mb-2">
        <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
        <div className={`w-6 h-6 rounded-md flex items-center justify-center ring-1 ${colors.iconBg}`}>
          {icon}
        </div>
      </div>
      <div className="flex items-baseline gap-1">
        <span
          className={`text-xl font-extrabold tabular-nums leading-none ${colors.text} ${colors.glow}`}
          style={{ fontFamily: 'ui-monospace, monospace' }}
        >
          {value}
        </span>
        {unit && <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{unit}</span>}
      </div>
    </div>
  );
}

// SVG icons for the meter stats
const IconBolt = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);
const IconClock = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
  </svg>
);
const IconVoltage = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 4v16M18 4v16M6 12h12M9 8h.01M15 16h.01" />
  </svg>
);
const IconCurrent = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12h3l3-9 6 18 3-9h3" />
  </svg>
);

// Format duration in mm:ss or hh:mm
function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function DeviceDetailPanel({ device, onStart, onStop, pending }: {
  device: BorneDevice; onStart: (sn: string) => void; onStop: (sn: string, txId: number) => void; pending: boolean;
}) {
  const { t: tr } = useI18n();
  const isCharging = device.activeTransactionId !== null;
  const isAvailable = device.device_status === 'active' || device.device_status === 'online' || device.device_status === 'Available';
  const statusInfo = getStatusInfo(device.ocppStatus || device.device_status, isCharging, tr);
  const img = getDeviceImg(device.device_sn);
  const modelLabel = img?.includes('wattelgo') ? 'WattelGo' : img?.includes('wattelwall') ? 'WattelWall' : tr('chg_model_generic');

  // Live duration ticker
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isCharging || !device.chargeStartedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isCharging, device.chargeStartedAt]);

  const durationMs = isCharging && device.chargeStartedAt ? now - device.chargeStartedAt : 0;
  const voltageV = isCharging ? 230 : 0;  // standard FR
  const currentA = voltageV > 0 ? device.currentPowerW / voltageV : 0;
  const energyKwh = (device.sessionEnergyWh || 0) / 1000;
  const co2Kg = energyKwh * 0.052;  // FR grid factor

  return (
    <div className="space-y-4 sticky top-6">
      {/* === Identity card === */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-5 text-white relative overflow-hidden">
          {isCharging && (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/20 to-transparent animate-pulse" />
          )}
          <div className="relative flex items-center gap-3">
            {img ? (
              <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center p-2">
                <img src={img} alt={modelLabel} className="h-full w-full object-contain" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs text-white/60 uppercase tracking-wider">{modelLabel}</p>
              <p className="font-bold text-lg truncate">{device.device_name || device.device_sn}</p>
              <p className="text-xs text-white/70 font-mono truncate">{device.device_sn}</p>
            </div>
          </div>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3 text-sm">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-zce-gray-muted font-semibold">{tr('chg_status')}</span>
            <span className={`mt-1 inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full w-fit ${statusInfo.bg} ${statusInfo.text}`}>
              <span className={`w-2 h-2 rounded-full ${statusInfo.dot}`} />
              {statusInfo.label}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider text-zce-gray-muted font-semibold">{tr('pg_firmware')}</span>
            <span className="mt-1 text-sm font-medium text-zce-navy">{device.firmware_version || '—'}</span>
          </div>
        </div>
      </div>

      {/* === PRO METER DASHBOARD (dark instrument panel) === */}
      <div className="relative bg-gradient-to-br from-[#1f4e7a] via-[#1a4267] to-[#163654] rounded-2xl shadow-xl border border-[#2a5a8a] overflow-hidden">
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.18] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.35) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        {/* Halo when charging */}
        {isCharging && (
          <div className="absolute -inset-px rounded-2xl pointer-events-none">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl" />
          </div>
        )}

        {/* Meter header bar */}
        <div className={`relative px-4 py-2.5 border-b ${isCharging ? 'bg-gradient-to-r from-emerald-600/90 to-emerald-700/90 border-emerald-800' : 'bg-[#143150]/60 border-[#2a5a8a]'} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isCharging ? 'bg-white animate-pulse shadow-[0_0_8px_rgba(255,255,255,0.8)]' : 'bg-white/40'}`} />
              <span className="text-[11px] font-bold uppercase tracking-widest">
                {isCharging ? tr('chg_charging_in_progress') : tr('chg_meter_ready')}
              </span>
            </div>
            <span className="text-[10px] font-mono opacity-80">
              {isCharging && device.activeTransactionId ? `TX #${device.activeTransactionId}` : tr('pg_chg_idle')}
            </span>
          </div>
          {isCharging && (
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-60 animate-pulse" />
          )}
        </div>

        {/* Power Gauge (centerpiece) */}
        <div className="relative px-4 pt-5 pb-3">
          <PowerGauge powerW={device.currentPowerW || 0} charging={isCharging} />
        </div>

        {/* Stats grid */}
        <div className="relative px-4 pb-4 grid grid-cols-2 gap-2.5">
          <MeterStat
            label={tr('chg_session_energy')}
            value={energyKwh.toFixed(3)}
            unit="kWh"
            color="emerald"
            icon={<IconBolt />}
          />
          <MeterStat
            label={tr('chg_duration')}
            value={isCharging ? formatDuration(durationMs) : '—'}
            unit=""
            color="navy"
            icon={<IconClock />}
          />
          <MeterStat
            label={tr('chg_voltage')}
            value={voltageV > 0 ? voltageV.toFixed(0) : '—'}
            unit="V"
            color="blue"
            icon={<IconVoltage />}
          />
          <MeterStat
            label={tr('chg_current')}
            value={currentA > 0 ? currentA.toFixed(2) : '—'}
            unit="A"
            color="orange"
            icon={<IconCurrent />}
          />
        </div>

        {/* CO2 footer when charging */}
        {isCharging && energyKwh > 0 && (
          <div className="relative mx-4 mb-4 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between backdrop-blur">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
              <span className="text-[11px] font-semibold text-emerald-300 uppercase tracking-wider">{tr('chg_co2_emitted')}</span>
            </div>
            <span className="text-sm font-bold text-emerald-300 tabular-nums" style={{ fontFamily: 'ui-monospace, monospace' }}>{co2Kg.toFixed(3)} kg</span>
          </div>
        )}

        {/* Progress bar at bottom (visual energy flow) */}
        {isCharging && (
          <div className="relative h-1 bg-slate-800 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400 to-transparent animate-pulse" />
          </div>
        )}
      </div>

      {/* === Commandes OCPP === */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </span>
          {tr('chg_ocpp_commands')}
        </h4>
        <div className="space-y-2">
          <button
            onClick={() => onStart(device.device_sn)}
            disabled={pending || !isAvailable || isCharging}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition disabled:opacity-40 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500"
          >
            {pending ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.84A1 1 0 005 3.5v13a1 1 0 001.5.866l11-6.5a1 1 0 000-1.732l-11-6.5z" />
                </svg>
                {tr('chg_start_charging')}
              </>
            )}
          </button>
          <button
            onClick={() => onStop(device.device_sn, device.activeTransactionId!)}
            disabled={pending || !isCharging}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition disabled:opacity-40 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500"
          >
            {pending ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 4h10v12H5z" />
                </svg>
                {tr('chg_stop_charging')}
              </>
            )}
          </button>
        </div>
        <p className="text-[10px] text-zce-gray-muted mt-3 text-center font-mono">
          OCPP 1.6 · RemoteStart/StopTransaction
        </p>
      </div>
    </div>
  );
}

export default function BornesPage() {
  const { t, locale } = useI18n();
  const [devices, setDevices] = useState<BorneDevice[]>([]);
  const [kpis, setKpis] = useState<KpiData | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [commandLoading, setCommandLoading] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilterValue>({ range: '7d', days: 7 });
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedSn, setSelectedSn] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'charging' | 'offline'>('all');
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [orgBornes, setOrgBornes] = useState<OrgBorne[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormName, setAddFormName] = useState('');
  const [addFormSn, setAddFormSn] = useState('');
  const [addFormLoading, setAddFormLoading] = useState(false);
  const [addFormError, setAddFormError] = useState<string | null>(null);

  const closeAddForm = () => {
    setShowAddForm(false);
    setAddFormName('');
    setAddFormSn('');
    setAddFormError(null);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();
  void user;

  // Auto-dismiss toast after 5s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // Load org bornes
  useEffect(() => {
    getOrgBornes().then(res => {
      if (res.success && res.data) setOrgBornes(res.data);
    }).catch(() => {});
  }, []);

  const handleAddBorne = async () => {
    if (!addFormSn.trim() || !addFormName.trim()) return;
    setAddFormLoading(true);
    setAddFormError(null);
    try {
      const res = await addOrgBorne(addFormSn.trim(), addFormName.trim());
      if (res.success) {
        setToast({ type: 'success', text: t('chg_toast_added', { name: addFormName }) });
        closeAddForm();
        // Refresh org bornes list
        const r = await getOrgBornes();
        if (r.success && r.data) setOrgBornes(r.data);
        // Reload devices to show the new borne
        loadDevices();
      } else {
        // Keep the modal open so the user can correct the serial number
        setAddFormError(res.error || t('chg_err_add'));
      }
    } catch (err: any) {
      // Détail technique en console uniquement ; message lisible pour l'utilisateur.
      console.error('[Bornes] add error:', err);
      setAddFormError(t('chg_err_network'));
    } finally {
      setAddFormLoading(false);
    }
  };

  const handleRemoveBorne = async (id: number) => {
    if (!confirm(t('chg_confirm_remove'))) return;
    try {
      const res = await removeOrgBorne(id);
      if (res.success) {
        setOrgBornes(prev => prev.filter(b => b.id !== id));
        setToast({ type: 'info', text: t('chg_toast_removed') });
      } else {
        setToast({ type: 'error', text: `❌ ${res.error || t('chg_err_remove')}` });
      }
    } catch (err: any) {
      // Détail technique en console uniquement ; message lisible pour l'utilisateur.
      console.error('[Bornes] remove error:', err);
      setToast({ type: 'error', text: `❌ ${t('chg_err_network')}` });
    }
  };

  const loadDevices = useCallback(async () => {
    try {
      setLoadingList(true);
      const sessions = loadSessions();
      const [devicesRes, kpisRes] = await Promise.all([
        otaApi.getDevices(),
        wattelzceApi.getKpis().catch(() => ({ data: null })),
      ]);
      const devicesWithState: BorneDevice[] = (devicesRes.data || []).map(d => {
        const s = sessions[d.device_sn];
        return {
          ...d,
          activeTransactionId: s?.transactionId ?? null,
          currentPowerW: 0,
          ocppStatus: d.device_status,
          chargeStartedAt: s?.chargeStartedAt ?? null,
          sessionEnergyWh: s?.sessionEnergyWh ?? 0,
          lastEnergyTickAt: s?.lastTickAt ?? null,
        };
      });
      setDevices(devicesWithState);
      if (kpisRes.data) setKpis(kpisRes.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err: any) {
      // Ignore rate-limit errors (429) silently, just keep previous data
      if (err.message?.includes('429')) {
        console.warn('[Bornes] Rate limited (429), retrying later...');
      } else {
        setError(err.message || translate('chg_err_loading'));
      }
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (devices.length === 0) return;
    devices.forEach(async (d) => {
      try {
        const res = await getDeviceStatus(d.device_sn);
        if (res.success && res.data) {
          setDevices(prev => prev.map(dev => {
            if (dev.device_sn !== d.device_sn) return dev;
            // ⚠ Ne pas écraser une session locale active si le backend renvoie null
            const backendTxId = res.data.transactionId ?? null;
            const keepLocalTx = dev.activeTransactionId !== null && backendTxId === null;
            return {
              ...dev,
              ocppStatus: res.data.status || dev.device_status,
              activeTransactionId: keepLocalTx ? dev.activeTransactionId : backendTxId,
              currentPowerW: res.data.currentPowerW != null && res.data.currentPowerW > 0 ? res.data.currentPowerW : dev.currentPowerW,
            };
          }));
        }
      } catch { /* ignore */ }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices.length]);

  useEffect(() => {
    loadDevices();
    const interval = setInterval(loadDevices, 30_000);
    return () => clearInterval(interval);
  }, [loadDevices]);

  // ─── Real OCPP session status poller (inspired by Flutter ChargingBloc) ──────
  // Polls backend every 10s for real energy/power from OCPP MeterValues.
  // The backend reads from charging_transactions + Redis OCPP cache.
  useEffect(() => {
    const fetchSessionStatus = async () => {
      const charging = devices.filter(d => d.activeTransactionId !== null);
      if (charging.length === 0) return;
      try {
        const sns = charging.map(d => d.device_sn);
        const res = await getChargingSessionStatus(sns);
        if (!res.success || !res.data || res.data.length === 0) return;

        const sessions = loadSessions();
        setDevices(prev => prev.map(dev => {
          if (!dev.activeTransactionId) return dev;
          const session = res.data!.find(s => s.deviceSn === dev.device_sn);
          if (!session) return dev;

          const s = sessions[dev.device_sn];
          if (!s) return dev;

          // Use real OCPP energy from backend (Wh)
          const sessionWh = session.currentEnergyWh || 0;
          const powerW = session.currentPowerW || 0;

          // Update persisted session with real values
          // Mark startEnergyWh=0 so the fallback integrator stops simulating
          s.startEnergyWh = 0;
          s.lastEnergyWh = sessionWh;
          s.sessionEnergyWh = sessionWh;
          s.lastTickAt = Date.now();
          sessions[dev.device_sn] = s;

          return {
            ...dev,
            currentPowerW: powerW > 0 ? powerW : dev.currentPowerW,
            sessionEnergyWh: sessionWh,
            lastEnergyTickAt: s.lastTickAt,
          };
        }));
        saveSessions(sessions);
      } catch (err) {
        console.warn('[Bornes] Session status poll error:', err);
      }
    };

    // Also poll individual device status for power (from OCPP server directly)
    const fetchDeviceStatuses = async () => {
      const charging = devices.filter(d => d.activeTransactionId !== null);
      if (charging.length === 0) return;
      for (const d of charging) {
        try {
          const res = await getDeviceStatus(d.device_sn);
          if (res.success && res.data) {
            setDevices(prev => prev.map(dev => {
              if (dev.device_sn !== d.device_sn) return dev;
              return {
                ...dev,
                currentPowerW: res.data.currentPowerW > 0 ? res.data.currentPowerW : dev.currentPowerW,
                // Also update energy if returned by OCPP status endpoint
                ...(res.data.energyWh != null && res.data.energyWh > 0
                  ? { sessionEnergyWh: res.data.energyWh }
                  : {}),
              };
            }));
          }
        } catch { /* ignore */ }
      }
    };

    // Initial fetch + periodic polling (like Flutter's 10s polling timer)
    fetchSessionStatus();
    fetchDeviceStatuses();
    const t1 = setInterval(fetchSessionStatus, 10_000);
    const t2 = setInterval(fetchDeviceStatuses, 10_000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [devices.length]); // re-arm when device list ready

  // NOTE: Fallback energy simulation REMOVED.
  // Energy data now comes exclusively from the OCPP session status poller above.
  // The Flutter app does the same: it shows 0 until the first real OCPP MeterValue arrives.

  const filtered = useMemo(() => {
    // Only show bornes that belong to the user's organization
    const orgSnSet = new Set(orgBornes.map(b => b.device_sn));
    const orgNameMap = new Map(orgBornes.map(b => [b.device_sn, b.borne_name]));
    let result = devices.filter(d => orgSnSet.has(d.device_sn)).map(d => ({
      ...d,
      device_name: orgNameMap.get(d.device_sn) || d.device_name,
    }));
    if (statusFilter !== 'all') {
      result = result.filter(d => {
        const isCharging = d.activeTransactionId !== null;
        const isAvailable = d.device_status === 'active' || d.device_status === 'online' || d.device_status === 'Available';
        if (statusFilter === 'charging') return isCharging;
        if (statusFilter === 'available') return isAvailable && !isCharging;
        if (statusFilter === 'offline') return !isAvailable && !isCharging;
        return true;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d => d.device_sn?.toLowerCase().includes(q) || d.device_name?.toLowerCase().includes(q));
    }
    return result;
  }, [devices, search, statusFilter, orgBornes]);

  const orgDevices = useMemo(() => {
    const orgSnSet = new Set(orgBornes.map(b => b.device_sn));
    return devices.filter(d => orgSnSet.has(d.device_sn));
  }, [devices, orgBornes]);

  const computedKpis = useMemo(() => ({
    total: orgDevices.length,
    active: orgDevices.filter(d => d.device_status === 'active' || d.device_status === 'online' || d.device_status === 'Available').length,
    charging: orgDevices.filter(d => d.activeTransactionId !== null).length,
    totalPowerKw: orgDevices.reduce((sum, d) => sum + (d.currentPowerW || 0), 0) / 1000,
  }), [orgDevices]);

  const selectedDevice = useMemo(() => {
    const d = devices.find(d => d.device_sn === selectedSn) || null;
    if (!d) return null;
    const orgName = orgBornes.find(b => b.device_sn === d.device_sn)?.borne_name;
    return orgName ? { ...d, device_name: orgName } : d;
  }, [devices, selectedSn, orgBornes]);

  const handleStart = async (deviceSn: string) => {
    setCommandLoading(deviceSn);
    setToast({ type: 'info', text: t('chg_toast_sending_start', { sn: deviceSn }) });
    try {
      // 🚀 OPTIMISTIC START — démarre la jauge immédiatement (mode démo si OCPP non connecté)
      const startedAt = Date.now();
      const optimisticTxId = Math.floor(Date.now() / 1000) % 100000; // ID local temporaire
      // Persist session — survit à navigation, refresh, logout
      startSession({
        deviceSn,
        transactionId: optimisticTxId,
        chargeStartedAt: startedAt,
        startEnergyWh: null,        // sera initialisé à la 1ère lecture meter
        lastEnergyWh: null,
        sessionEnergyWh: 0,
        lastTickAt: startedAt,
        fallbackPowerW: 7400,
      });
      setDevices(prev => prev.map(d => d.device_sn === deviceSn ? {
        ...d,
        activeTransactionId: optimisticTxId,
        chargeStartedAt: startedAt,
        sessionEnergyWh: 0,
        lastEnergyTickAt: startedAt,
      } : d));

      const result = await startTransaction(deviceSn);
      console.log('[OCPP Start]', deviceSn, result);
      if (result.success && result.data) {
        const txId = result.data.transactionId ?? optimisticTxId;
        const provisional = result.data.result?.provisional;
        // Update with real transaction ID from backend
        setDevices(prev => prev.map(d => d.device_sn === deviceSn ? {
          ...d,
          activeTransactionId: txId,
        } : d));
        updateSession(deviceSn, { transactionId: txId });
        setToast({
          type: 'success',
          text: provisional
            ? t('chg_toast_started_provisional', { tx: txId })
            : t('chg_toast_started', { sn: deviceSn, tx: txId })
        });
      } else {
        // OCPP a refusé → la borne n'est pas connectée, on annule la session
        console.warn('[OCPP Start] Backend refused, borne offline — cancelling session');
        endSession(deviceSn);
        setDevices(prev => prev.map(d => d.device_sn === deviceSn ? {
          ...d,
          activeTransactionId: null,
          chargeStartedAt: null,
          sessionEnergyWh: 0,
          lastEnergyTickAt: null,
          currentPowerW: 0,
        } : d));
        setToast({
          type: 'error',
          text: t('chg_toast_start_offline', { sn: deviceSn, reason: result.error || t('chg_reason_not_connected') })
        });
      }
    } catch (err: any) {
      console.error('[OCPP Start] error:', err);
      // Erreur réseau : on rollback l'optimistic
      endSession(deviceSn);
      setDevices(prev => prev.map(d => d.device_sn === deviceSn ? {
        ...d,
        activeTransactionId: null,
        chargeStartedAt: null,
        sessionEnergyWh: 0,
        lastEnergyTickAt: null,
        currentPowerW: 0,
      } : d));
      setToast({ type: 'error', text: t('chg_toast_network_error', { msg: err.message }) });
    } finally {
      setCommandLoading(null);
    }
  };

  const handleStop = async (deviceSn: string, transactionId: number) => {
    setCommandLoading(deviceSn);
    setToast({ type: 'info', text: t('chg_toast_stopping', { sn: deviceSn }) });

    // 🛑 OPTIMISTIC STOP — coupe la jauge tout de suite
    const stopLocal = () => {
      endSession(deviceSn);
      setDevices(prev => prev.map(d => d.device_sn === deviceSn ? {
        ...d,
        activeTransactionId: null,
        currentPowerW: 0,
        chargeStartedAt: null,
        lastEnergyTickAt: null,
      } : d));
    };

    try {
      const result = await stopTransaction(deviceSn, transactionId);
      console.log('[OCPP Stop]', deviceSn, result);
      stopLocal();
      if (result.success) {
        setToast({ type: 'success', text: t('chg_toast_stopped', { sn: deviceSn }) });
      } else {
        setToast({
          type: 'info',
          text: t('chg_toast_local_stop_ocpp', { reason: result.error || t('chg_reason_session_not_found') })
        });
      }
    } catch (err: any) {
      console.error('[OCPP Stop] error:', err);
      stopLocal();
      setToast({ type: 'info', text: t('chg_toast_local_stop_network', { msg: err.message }) });
    } finally {
      setCommandLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=1600&h=520&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/97 via-slate-900/92 to-cyan-950/82" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-transparent to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="absolute -top-32 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 right-0 w-[36rem] h-[36rem] rounded-full bg-teal-400/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-screen-2xl mx-auto px-6 py-7">
          {/* Title row */}
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div className="flex items-start gap-5">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-400 to-teal-500 blur-xl opacity-60" />
                <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center shadow-xl ring-1 ring-white/20 text-white">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-[11px] font-bold uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {t('pg_live_badge')}
                  </span>
                  <span className="text-xs text-white/55">{t('chg_last_update', { time: lastUpdate.toLocaleTimeString(locale) })}</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white drop-shadow-lg">
                  {t('chg_title_prefix')}{' '}
                  <span className="bg-gradient-to-r from-cyan-300 via-teal-300 to-emerald-300 bg-clip-text text-transparent">
                    {t('chg_title_highlight')}
                  </span>
                </h1>
                <p className="text-slate-200 text-sm mt-1.5 drop-shadow">
                  {t('chg_subtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={loadDevices}
              disabled={loadingList}
              className="shrink-0 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-white font-semibold rounded-xl shadow-lg ring-1 ring-white/20 transition-all disabled:opacity-50"
            >
              <svg className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('chg_refresh')}
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="shrink-0 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white font-semibold rounded-xl shadow-lg ring-1 ring-white/20 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('chg_add_charger')}
            </button>
            </div>
          </div>

          {/* KPI glassmorphism cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {([
              {
                label: t('chg_kpi_total'), value: computedKpis.total,
                sub: t('chg_kpi_available_sub', { n: computedKpis.active }),
                pct: 100,
                bar: 'from-cyan-400 to-teal-400',
                iconBg: 'bg-slate-500/30 text-slate-200',
                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
              },
              {
                label: t('chg_kpi_available'), value: computedKpis.active,
                sub: t('chg_kpi_ready'),
                pct: computedKpis.total > 0 ? Math.round((computedKpis.active / computedKpis.total) * 100) : 0,
                bar: 'from-emerald-400 to-teal-400',
                iconBg: 'bg-emerald-500/30 text-emerald-300',
                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
              },
              {
                label: t('chg_status_charging'), value: computedKpis.charging,
                sub: t('chg_kpi_active_kw', { kw: computedKpis.totalPowerKw.toFixed(1) }),
                pct: computedKpis.total > 0 ? Math.round((computedKpis.charging / computedKpis.total) * 100) : 0,
                bar: 'from-amber-400 to-orange-400',
                iconBg: 'bg-amber-500/30 text-amber-300',
                pulse: computedKpis.charging > 0,
                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
              },
              {
                label: t('chg_kpi_total_energy'),
                value: kpis?.totalEnergyKwh ? `${(kpis.totalEnergyKwh / 1000).toFixed(1)}` : '—',
                sub: kpis?.totalEnergyKwh ? t('chg_kpi_mwh_cumulative') : t('chg_kpi_kwh_cumulative'),
                pct: kpis?.totalEnergyKwh ? 68 : 0,
                bar: 'from-blue-400 to-indigo-400',
                iconBg: 'bg-blue-500/30 text-blue-300',
                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
              },
            ] as const).map((card, i) => (
              <div key={i} className="bg-white/[0.08] backdrop-blur-sm border border-white/[0.13] rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-white/55 uppercase tracking-widest">{card.label}</span>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${card.iconBg}`}>{card.icon}</div>
                </div>
                <p className="text-3xl font-bold text-white tabular-nums">{card.value}</p>
                <p className="text-xs text-white/45 mt-0.5">{card.sub}</p>
                <div className="mt-2.5 h-[3px] bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${card.bar} rounded-full transition-all duration-1000 ${'pulse' in card && card.pulse ? 'animate-pulse' : ''}`}
                    style={{ width: `${card.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 py-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl flex items-center gap-2 text-sm">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            <span>{error}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-zce-navy flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-zce-navy font-heading">
                  {t('chg_energy_consumption')}
                  {selectedDevice && (
                    <span className="ml-2 text-zce-blue">— {selectedDevice.device_name || selectedDevice.device_sn}</span>
                  )}
                </h3>
                <p className="text-xs text-zce-gray-muted">
                  {selectedDevice
                    ? t('chg_meter_history', { sn: selectedDevice.device_sn })
                    : t('chg_select_charger_history')}
                </p>
              </div>
            </div>
            <ChartTimeFilter value={timeFilter} onChange={setTimeFilter} />
          </div>
          <EnergyChart
            filter={timeFilter}
            deviceId={selectedDevice?.device_id ?? null}
            deviceLabel={selectedDevice?.device_name || selectedDevice?.device_sn || null}
          />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {([
                { key: 'all', label: t('chg_filter_all'), count: orgDevices.length },
                { key: 'available', label: t('chg_kpi_available'), count: computedKpis.active - computedKpis.charging },
                { key: 'charging', label: t('chg_status_charging'), count: computedKpis.charging },
                { key: 'offline', label: t('chg_status_offline'), count: orgDevices.length - computedKpis.active },
              ] as const).map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setStatusFilter(opt.key)}
                  className={`px-3.5 py-1.5 text-sm rounded-xl font-medium transition-all ${statusFilter === opt.key ? 'bg-cyan-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {opt.label}
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${statusFilter === opt.key ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {opt.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('chg_search_placeholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl w-72 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500 bg-slate-50"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            {loadingList && devices.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center col-span-2">
                <div className="animate-spin h-10 w-10 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto mb-3" />
                <p className="text-slate-400 text-sm">{t('chg_loading_chargers')}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center col-span-2">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-3 text-slate-400">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                </div>
                <p className="text-slate-500 font-medium text-sm">
                  {orgBornes.length === 0
                    ? t('chg_empty_org')
                    : t('chg_empty_search')}
                </p>
                {orgBornes.length === 0 && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="mt-3 px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                  >
                    + {t('chg_add_charger')}
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filtered.map(device => {
                  const orgBorne = orgBornes.find(b => b.device_sn === device.device_sn);
                  return (
                    <BorneCard
                      key={device.device_id}
                      device={device}
                      selected={selectedSn === device.device_sn}
                      onClick={() => setSelectedSn(device.device_sn)}
                      onRemove={orgBorne ? () => handleRemoveBorne(orgBorne.id) : undefined}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            {selectedDevice ? (
              <DeviceDetailPanel
                device={selectedDevice}
                onStart={handleStart}
                onStop={handleStop}
                pending={commandLoading === selectedDevice.device_sn}
              />
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center sticky top-6">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                </div>
                <h3 className="font-semibold text-slate-700 mb-1">{t('chg_select_charger')}</h3>
                <p className="text-sm text-slate-400">{t('chg_select_charger_detail_hint')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Add Borne Modal ── */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={closeAddForm}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-100" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-5 bg-gradient-to-r from-zce-navy to-zce-navy-light">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white font-heading">{t('chg_add_charger')}</h2>
                  <p className="text-blue-200/80 text-xs">{t('chg_add_subtitle')}</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-5">
              <p className="text-sm text-zce-gray-muted">
                {t('chg_add_intro')}
              </p>
              <div>
                <label className="block text-sm font-medium text-zce-navy mb-1.5">{t('chg_field_name')}</label>
                <input
                  type="text"
                  value={addFormName}
                  onChange={e => setAddFormName(e.target.value)}
                  placeholder={t('chg_field_name_ph')}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-zce-navy focus:outline-none focus:ring-2 focus:ring-zce-blue/30 focus:border-zce-blue transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zce-navy mb-1.5">{t('chg_field_sn')}</label>
                <input
                  type="text"
                  value={addFormSn}
                  onChange={e => { setAddFormSn(e.target.value); setAddFormError(null); }}
                  placeholder={t('chg_field_sn_ph')}
                  aria-invalid={!!addFormError}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm text-zce-navy focus:outline-none focus:ring-2 transition-all ${
                    addFormError
                      ? 'border-rose-400 focus:ring-rose-300/40 focus:border-rose-500'
                      : 'border-gray-200 focus:ring-zce-blue/30 focus:border-zce-blue'
                  }`}
                />
                {addFormError && (
                  <p role="alert" className="mt-2 text-sm text-rose-600 flex items-start gap-1.5">
                    <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <span>{addFormError}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={closeAddForm}
                className="px-5 py-2.5 text-sm font-medium text-zce-navy bg-white border border-gray-200 hover:bg-gray-100 rounded-xl transition-colors shadow-sm"
              >
                {t('chg_cancel')}
              </button>
              <button
                onClick={handleAddBorne}
                disabled={addFormLoading || !addFormSn.trim() || !addFormName.trim()}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-zce-blue hover:bg-blue-600 rounded-xl shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                {addFormLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('chg_add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 max-w-md">
          <div className={`px-5 py-4 rounded-xl shadow-2xl border-l-4 backdrop-blur ${
            toast.type === 'success' ? 'bg-emerald-50 border-emerald-500 text-emerald-900' :
            toast.type === 'error' ? 'bg-red-50 border-red-500 text-red-900' :
            'bg-blue-50 border-blue-500 text-blue-900'
          }`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none">
                {toast.type === 'success' ? '✅' : toast.type === 'error' ? '⚠️' : 'ℹ️'}
              </span>
              <p className="text-sm font-medium flex-1 pt-0.5">{toast.text}</p>
              <button
                onClick={() => setToast(null)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none -mt-0.5"
                aria-label={t('chg_close')}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
